import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import {
  DocumentSearchApiService,
  DocumentSearchQuery,
  OcrSearchResult,
} from './document-search-api.service';
import { mapValues } from '@ps-gui/shared-utils';
import { sortBy } from '@vydaps/gui-commons/arrays';
import { ToastService } from '@ps-gui/shared-core';

@Injectable({
  providedIn: 'root',
})
export class DocumentSearchService {
  private readonly _foundWords = signal<OcrSearchResult[] | undefined>(undefined);

  private readonly _currentFoundWordIndex = linkedSignal<number | undefined>(() => {
    const words = this._foundWords();
    return words && words.length > 0 ? 0 : undefined;
  });

  readonly currentFoundWordIndex = this._currentFoundWordIndex.asReadonly();

  readonly foundWords = this._foundWords.asReadonly();

  readonly foundWordsByPageId = computed(() =>
    mapValues(
      Object.groupBy(this.foundWords() ?? [], (word) => word.pageId),
      (_, words) =>
        sortBy(
          words,
          (rect) => rect.wordRectTop,
          (rect) => rect.wordRectLeft,
        ),
    ),
  );

  private readonly searchApiClient = inject(DocumentSearchApiService);
  private readonly toastService = inject(ToastService);

  private lastSearchQuery: DocumentSearchQuery | undefined;

  search(query: DocumentSearchQuery) {
    if (this.isNewSearchQuery(query)) {
      this.toastService
        .runWithToast(
          this.searchApiClient.searchText(query),
          'Suche läuft',
          'Suche beendet',
          'Fehler bei der Suche',
        )
        .subscribe((result) => this._foundWords.set(result));

      this.lastSearchQuery = query;
    }

    this.iterateFoundWords();
  }

  private isNewSearchQuery(query: DocumentSearchQuery) {
    return (
      this.lastSearchQuery === undefined ||
      JSON.stringify(query) !== JSON.stringify(this.lastSearchQuery)
    );
  }

  private iterateFoundWords() {
    this._currentFoundWordIndex.update((oldIndex) => {
      const foundWordsCount = this.foundWords()?.length;
      if (!foundWordsCount) return undefined;
      return ((oldIndex ?? 0) + 1) % foundWordsCount;
    });
  }

  clearSearch() {
    this._foundWords.set(undefined);
    this.lastSearchQuery = undefined;
  }
}
